import { useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '@/components/Avatar/Avatar';
import { Chip } from '@/components/Chip/Chip';
import { Button } from '@/components/Button/Button';
import { InfoHint } from '@/components/InfoHint/InfoHint';
import { Alert } from '@/components/Alert/Alert';
import { ConfirmDialog } from '@/components/ConfirmDialog/ConfirmDialog';
import { Popover } from '@/components/Popover/Popover';
import { Tabs } from '@/components/Tabs/Tabs';
import { ApiError } from '@/lib/api';
import { t } from '@/lib/i18n';
import { formatDate, formatPersonName, formatNumber } from '@/lib/format';
import { memberStatus } from '../utils';
import { useAuthStore, usePermission } from '@/stores/auth';
import { useInfoHintPulse } from '@/features/onboarding';
import { DescribeJobSheet, JobsTable, useJobsList, type JobListItem } from '@/features/jobs';
import { useUploadAttachment } from '@/features/attachments/hooks';
import { MemberConnectorsTab, ConnectorInvoker, useMyGrants } from '@/features/connectors';
import {
  useArchiveMember,
  useLinkMemberAttachment,
  useMember,
  useMemberAttachments,
  useUnlinkMemberAttachment,
} from '../hooks';
import type { Member } from '../schemas';
import { RecommendedJobsCard } from './RecommendedJobsCard';
import { WorkHistoryCoach } from './WorkHistoryCoach';
import { AddedMemberBanner } from './HireCelebrationBanner';
import { StatusPill } from '@/components/StatusPill/StatusPill';
import styles from './MemberDetail.module.css';

export function MemberDetail() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const canManage = useAuthStore((s) => s.permissions.has('manage_members'));
  const canManageGrants = useAuthStore((s) => s.permissions.has('manage_connector_grants'));
  const canCreateJob = usePermission('create_job');
  const myGrantsQuery = useMyGrants();
  const isOwnProfile = Boolean(
    myGrantsQuery.data?.memberId && myGrantsQuery.data.memberId === memberId,
  );
  const welcomedAt = useAuthStore((s) => s.tenantUser?.welcomedAt);
  const celebratedIds = useAuthStore((s) => s.tenantUser?.celebratedMemberIds ?? []);
  const { data, isLoading, error } = useMember(memberId);
  const archive = useArchiveMember();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [describeOpen, setDescribeOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dismissedLocally, setDismissedLocally] = useState(false);
  const pulsing = useInfoHintPulse();
  const jobsQuery = useJobsList();
  const memberJobs = useMemo(
    () => (jobsQuery.data?.items ?? []).filter((j) => j.assigned_member.id === memberId),
    [jobsQuery.data?.items, memberId],
  );
  const jobsRunning = memberJobs.filter((j) => j.status === 'running').length;
  const jobsCompleted = memberJobs.filter((j) => j.status === 'completed').length;
  const status =
    data?.member.kind === 'ai' ? memberStatus({ id: memberId ?? '' }, memberJobs) : null;

  // Coach is AI-only — humans don't take Jobs, so an empty-history coach
  // would be meaningless for them.
  const isAiMember = data?.member.kind === 'ai';
  const workHistoryEmpty =
    isAiMember === true && !jobsQuery.isLoading && !jobsQuery.isError && memberJobs.length === 0;

  if (isLoading) {
    return (
      <div className={styles.empty} role="status" aria-live="polite">
        {t('Loading…')}
      </div>
    );
  }

  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className={styles.empty} role="alert">
        <h2 className={styles.emptyHeading}>{t('Member not found')}</h2>
        <p className={styles.emptyBody}>
          {t('That Member may have been let go, or never existed in this workspace.')}
        </p>
        <Link to="/team" className={styles.backLink}>
          <Button variant="secondary" size="md">
            {t('Back to team')}
          </Button>
        </Link>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={styles.empty} role="alert">
        {t("Couldn't load this Member.")}
      </div>
    );
  }

  const { member } = data;
  const prefillMember = { id: member.id, name: formatPersonName(member) };
  const isArchived = Boolean(member.archivedAt);
  const isAi = member.kind === 'ai';
  const primaryPosition = member.positions[0]?.name ?? t('Awaiting assignment');

  // BP-028: Eligibility check for hire celebration banner
  const showCelebration =
    welcomedAt === null &&
    member.createdVia === 'starter_signup' &&
    !celebratedIds.includes(member.id) &&
    !dismissedLocally;

  function onLetGo() {
    if (!member) return;
    setActionError(null);
    archive.mutate(
      { memberId: member.id },
      {
        onSuccess: () => {
          void navigate('/team');
        },
        onError: (err) => {
          setConfirmOpen(false);
          if (err instanceof ApiError && err.status === 404) {
            setActionError(t('This Member is no longer available.'));
          } else if (err instanceof ApiError && err.status === 403) {
            setActionError(t("You don't have permission to let go of Members."));
          } else {
            setActionError(t("Couldn't let go of this Member. Try again."));
          }
        },
      },
    );
  }

  return (
    <div className={styles.page}>
      {showCelebration ? (
        <AddedMemberBanner member={member} onDismissed={() => setDismissedLocally(true)} />
      ) : null}

      {/* ─── Top bar ─────────────────────────────────────────────────── */}
      <div className={styles.topBar}>
        <Link to="/team" className={styles.backLink}>
          {'← '}
          {t('Back to team')}
        </Link>
      </div>

      {isArchived ? <Alert variant="info">{t('This Member has been let go.')}</Alert> : null}
      {actionError ? <Alert variant="error">{actionError}</Alert> : null}

      {/* ─── Hero card ─────────────────────────────────────────────── */}
      <section className={styles.hero}>
        {/* ··· menu — top-right corner */}
        {canManage && !isArchived ? (
          <div className={styles.heroMoreWrap}>
            <Popover
              open={moreOpen}
              onOpenChange={setMoreOpen}
              align="end"
              trigger={
                <button
                  type="button"
                  className={styles.heroMoreBtn}
                  aria-label={t('More options')}
                  aria-haspopup="menu"
                >
                  {'···'}
                </button>
              }
            >
              <div role="menu" className={styles.popoverMenu}>
                <Link
                  to={`/team/m/${encodeURIComponent(member.id)}/edit`}
                  role="menuitem"
                  className={styles.popoverMenuItem}
                  onClick={() => setMoreOpen(false)}
                >
                  {t('Edit member')}
                </Link>
                <div className={styles.popoverMenuSep} aria-hidden="true" />
                <button
                  type="button"
                  role="menuitem"
                  className={`${styles.popoverMenuItem} ${styles.popoverMenuItemDanger}`}
                  onClick={() => {
                    setMoreOpen(false);
                    setConfirmOpen(true);
                  }}
                  disabled={archive.isPending}
                >
                  {t('Let go…')}
                </button>
              </div>
            </Popover>
          </div>
        ) : null}

        {/* Avatar + identity row */}
        <div className={styles.heroMain}>
          <Avatar name={formatPersonName(member)} size="lg" kind={member.kind} />
          <div className={styles.heroBody}>
            <div className={styles.heroNameLine}>
              <h1 className={styles.heroName}>{formatPersonName(member)}</h1>
              <InfoHint
                label={t('{name}', { name: formatPersonName(member) })}
                tooltip={t(
                  'Everything {name} does — work history, what to chat about, how to assign more.',
                  { name: formatPersonName(member) },
                )}
                pulsing={pulsing}
              />
              {isAi ? (
                <Chip variant="ai" size="md">
                  {t('AI')}
                </Chip>
              ) : null}
            </div>
            <div className={styles.heroPosition}>{primaryPosition}</div>
            {status === 'working' ? (
              <StatusPill variant="success" dot>
                {t('Working · {n} {job}', {
                  n: jobsRunning,
                  job: jobsRunning === 1 ? t('job') : t('jobs'),
                })}
              </StatusPill>
            ) : null}
            {status === 'idle' ? <StatusPill variant="muted">{t('Idle')}</StatusPill> : null}
            {status === 'blocked' ? <StatusPill variant="danger">{t('Blocked')}</StatusPill> : null}
            {member.description ? (
              <p className={styles.heroDescription}>{member.description}</p>
            ) : null}
            <dl className={styles.statsRow}>
              <Stat
                label={t('On the team since')}
                value={formatDate(member.createdAt, { month: 'short', day: 'numeric' })}
              />
              <Stat label={t('Jobs completed')} value={formatNumber(jobsCompleted)} />
              {jobsRunning > 0 ? (
                <Stat label={t('Running now')} value={formatNumber(jobsRunning)} />
              ) : null}
            </dl>
          </div>
        </div>

        {/* Hero footer */}
        {isAi && !isArchived ? (
          <div className={styles.heroFooter}>
            <Link to={`/team/m/${encodeURIComponent(member.id)}/chat`}>
              <Button variant="primary" size="sm">
                {t('Chat')}
              </Button>
            </Link>
            {canCreateJob ? (
              <>
                <div className={styles.heroFooterDivider} aria-hidden="true" />
                <Popover
                  open={assignOpen}
                  onOpenChange={setAssignOpen}
                  align="start"
                  trigger={
                    <Button variant="primary" size="sm" aria-haspopup="menu">
                      {t('Assign work')} <span aria-hidden="true">{'▾'}</span>
                    </Button>
                  }
                >
                  <div role="menu" className={styles.popoverMenu}>
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.popoverMenuItem}
                      onClick={() => {
                        setAssignOpen(false);
                        setDescribeOpen(true);
                      }}
                    >
                      <div>
                        <div>{t('Describe a Job')}</div>
                        <div className={styles.popoverMenuItemDesc}>
                          {t('Tell {name} what you need in plain English', {
                            name: member.first_name,
                          })}
                        </div>
                      </div>
                    </button>
                    <div className={styles.popoverMenuSep} aria-hidden="true" />
                    <Link
                      to={`/jobs/new?member=${encodeURIComponent(member.id)}`}
                      role="menuitem"
                      className={styles.popoverMenuItem}
                      onClick={() => setAssignOpen(false)}
                    >
                      <div>
                        <div>{t('Pick from library')}</div>
                        <div className={styles.popoverMenuItemDesc}>
                          {t('Choose a saved Job Template')}
                        </div>
                      </div>
                    </Link>
                  </div>
                </Popover>
              </>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* ─── Tabs ──────────────────────────────────────────────────── */}
      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List aria-label={t('Member sections')}>
          <Tabs.Trigger value="overview">{t('Overview')}</Tabs.Trigger>
          <Tabs.Trigger value="work-history">{t('Work history')}</Tabs.Trigger>
          {isAi ? <Tabs.Trigger value="background">{t('Background')}</Tabs.Trigger> : null}
          {canManageGrants ? (
            <Tabs.Trigger value="connectors">{t('Connectors')}</Tabs.Trigger>
          ) : null}
          {isOwnProfile ? <Tabs.Trigger value="tools">{t('Tools')}</Tabs.Trigger> : null}
        </Tabs.List>

        {activeTab === 'overview' && (
          <Tabs.Content value="overview">
            <OverviewTab member={member} />
          </Tabs.Content>
        )}
        {activeTab === 'work-history' && (
          <Tabs.Content value="work-history">
            <WorkHistoryTab
              member={member}
              jobs={memberJobs}
              isLoading={jobsQuery.isLoading}
              isError={jobsQuery.isError}
              showCoach={workHistoryEmpty}
            />
          </Tabs.Content>
        )}
        {isAi && activeTab === 'background' && (
          <Tabs.Content value="background">
            <BackgroundTab member={member} />
          </Tabs.Content>
        )}
        {canManageGrants && activeTab === 'connectors' && (
          <Tabs.Content value="connectors">
            <MemberConnectorsTab memberId={memberId!} />
          </Tabs.Content>
        )}
        {isOwnProfile && activeTab === 'tools' && (
          <Tabs.Content value="tools">
            <ConnectorToolsTabContent memberId={memberId!} />
          </Tabs.Content>
        )}
      </Tabs.Root>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('Let go of {name}?', { name: formatPersonName(member) })}
        description={t(
          "They'll be removed from your active team. Their history stays around so you can look back, but they won't take on new work.",
        )}
        confirmVariant="danger"
        confirmLabel={t('Let go')}
        pendingLabel={t('Letting go…')}
        isPending={archive.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onLetGo}
      />
      {describeOpen ? (
        <DescribeJobSheet open onOpenChange={setDescribeOpen} prefillMember={prefillMember} />
      ) : null}
    </div>
  );
}

/* ─── Hero stats helper ──────────────────────────────────────────── */

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.stat}>
      <dt className={styles.statValue}>{value}</dt>
      <dd className={styles.statLabel}>{label}</dd>
    </div>
  );
}

/* ─── Tab content ────────────────────────────────────────────────── */

function OverviewTab({ member }: { member: Member }) {
  const hasPersonality = Boolean(member.personality);
  const hasSystemPrompt = Boolean(member.systemPrompt);
  const hasAssignments = member.positions.length > 0 || member.units.length > 0;
  const systemPromptHtml = useMemo(() => {
    if (!member.systemPrompt) return '';
    const raw = marked.parse(member.systemPrompt, { async: false });
    return DOMPurify.sanitize(raw);
  }, [member.systemPrompt]);

  return (
    <div className={styles.overviewGrid}>
      <div className={styles.overviewMain}>
        {hasPersonality ? (
          <section className={styles.section}>
            <h2 className={styles.sectionHeading}>{t('How they work')}</h2>
            <div className={styles.sectionCard}>
              <p className={styles.bodyText}>{member.personality}</p>
            </div>
          </section>
        ) : null}

        {hasSystemPrompt ? (
          <section className={styles.section}>
            <h2 className={styles.sectionHeading}>{t('Detailed instructions')}</h2>
            <div className={styles.sectionCard}>
              <div
                className={styles.markdown}
                dangerouslySetInnerHTML={{ __html: systemPromptHtml }}
              />
            </div>
          </section>
        ) : null}

        {!hasPersonality && !hasSystemPrompt ? (
          <div className={styles.tabEmpty}>
            <p className={styles.tabEmptyBody}>
              {t('No background brief yet — add one from the Edit page.')}
            </p>
          </div>
        ) : null}
      </div>

      <aside className={styles.overviewAside}>
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>{t('Where they work')}</h2>
          <div className={styles.sectionCard}>
            {hasAssignments ? (
              <>
                {member.units.length > 0 ? (
                  <div className={styles.subGroup}>
                    <div className={styles.eyebrow}>{t('Departments')}</div>
                    <div className={styles.chips}>
                      {member.units.map((u) => (
                        <Chip key={u.id} variant="neutral" size="md">
                          {u.name}
                        </Chip>
                      ))}
                    </div>
                  </div>
                ) : null}
                {member.positions.length > 0 ? (
                  <div className={styles.subGroup}>
                    <div className={styles.eyebrow}>{t('Positions')}</div>
                    <div className={styles.chips}>
                      {member.positions.map((p) => (
                        <Chip key={p.id} variant="neutral" size="md">
                          {p.name}
                        </Chip>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <p className={styles.bodyTextMuted}>
                {t('No positions or departments assigned yet.')}
              </p>
            )}
          </div>
        </section>

        {/* AI-only — humans don't take Jobs, so recommendations are meaningless. */}
        {member.kind === 'ai' && !member.archivedAt ? (
          <RecommendedJobsCard memberId={member.id} preassignMemberId={member.id} />
        ) : null}
      </aside>
    </div>
  );
}

interface WorkHistoryTabProps {
  member: Member;
  jobs: readonly JobListItem[];
  isLoading: boolean;
  isError: boolean;
  showCoach: boolean;
}

function WorkHistoryTab({ member, jobs, isLoading, isError, showCoach }: WorkHistoryTabProps) {
  if (showCoach) {
    return <WorkHistoryCoach memberId={member.id} memberName={member.first_name} />;
  }

  return (
    <JobsTable
      data={jobs}
      isLoading={isLoading}
      isError={isError}
      showAssignedColumn={false}
      emptyState={t('No Jobs yet for {name}.', { name: formatPersonName(member) })}
    />
  );
}

/* ─── Background tab — knowledge files + tool toggles ────────────── */

interface ToolGrant {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
}

const SCAFFOLD_TOOLS: readonly ToolGrant[] = [
  {
    id: 'read-inbox',
    label: 'Read inbox',
    description: 'See incoming email and notifications.',
    enabled: true,
  },
  {
    id: 'send-email-draft',
    label: 'Send email drafts',
    description: 'Prepare outgoing email for your review.',
    enabled: true,
  },
  {
    id: 'send-email-auto',
    label: 'Send email autonomously',
    description: 'Send without your review — off by default.',
    enabled: false,
  },
  {
    id: 'read-drive',
    label: 'Read drive',
    description: 'Access files in your connected storage.',
    enabled: true,
  },
  {
    id: 'write-drive',
    label: 'Write to drive',
    description: 'Create and edit files in your storage.',
    enabled: true,
  },
  {
    id: 'charge-card',
    label: 'Charge company card',
    description: 'Make purchases on behalf of the company.',
    enabled: false,
  },
] as const;

function BackgroundTab({ member }: { member: Member }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    data: filesData,
    isLoading: filesLoading,
    error: filesError,
  } = useMemberAttachments(member.id);
  const upload = useUploadAttachment();
  const link = useLinkMemberAttachment(member.id);
  const unlink = useUnlinkMemberAttachment(member.id);
  const isBusy = upload.isPending || link.isPending;

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      try {
        const { attachment } = await upload.mutateAsync({ file });
        await link.mutateAsync({ attachmentId: attachment.id });
      } catch {
        // individual file failures are non-fatal
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  }

  const fileItems = filesData?.items ?? [];

  return (
    <div className={styles.backgroundGrid}>
      <section className={styles.bgCard}>
        <div className={styles.eyebrow}>{t('Knowledge files')}</div>

        {filesLoading ? (
          <p className={styles.tabEmptyBody}>{t('Loading…')}</p>
        ) : filesError ? (
          <Alert variant="error">{t('Failed to load files.')}</Alert>
        ) : fileItems.length === 0 ? (
          <p className={styles.tabEmptyBody}>
            {t('Add files to give this member persistent knowledge.')}
          </p>
        ) : (
          <ul className={styles.fileList}>
            {fileItems.map((a) => (
              <li key={a.id} className={styles.fileRow}>
                <span className={styles.fileIcon} aria-hidden="true">
                  📄
                </span>
                <div className={styles.fileBody}>
                  <span className={styles.fileName}>{a.name}</span>
                  <div className={styles.fileMeta}>
                    {a.mime_type} · {formatBytes(a.size_bytes)}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.fileRemove}
                  onClick={() => unlink.mutate({ attachmentId: a.id })}
                  disabled={unlink.isPending}
                >
                  {t('Remove')}
                </button>
              </li>
            ))}
          </ul>
        )}

        <label className={styles.fileAdd}>
          <input
            ref={inputRef}
            type="file"
            multiple
            className={styles.fileInput}
            onChange={(e) => void handleFiles(e.target.files)}
            disabled={isBusy}
          />
          <span>{isBusy ? t('Uploading…') : t('+ Add file')}</span>
        </label>
      </section>

      <section className={styles.bgCard}>
        <div className={styles.eyebrow}>{t('Tools they can use')}</div>
        <ul className={styles.toolList}>
          {SCAFFOLD_TOOLS.map((tool) => (
            <li key={tool.id} className={styles.toolRow}>
              <div className={styles.toolInfo}>
                <span className={styles.toolLabel}>{tool.label}</span>
                <span className={styles.toolDescription}>{tool.description}</span>
              </div>
              <ToggleSwitch checked={tool.enabled} disabled label={tool.label} />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

interface ToggleSwitchProps {
  checked: boolean;
  disabled?: boolean;
  label: string;
}

function ConnectorToolsTabContent({ memberId: _memberId }: { memberId: string }) {
  const { data } = useMyGrants();
  const [activeConnectorId, setActiveConnectorId] = useState<string | null>(null);
  const connectors = data?.connectors ?? [];

  if (!connectors.length) {
    return (
      <div
        style={{
          padding: 'var(--space-4)',
          color: 'var(--color-text-subtle)',
          fontSize: 'var(--text-sm)',
        }}
      >
        {t("You haven't been granted access to any connectors yet. Ask your Admin.")}
      </div>
    );
  }

  if (activeConnectorId) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <button
          onClick={() => setActiveConnectorId(null)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 'var(--space-2) var(--space-4)',
            textAlign: 'left',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text-subtle)',
          }}
        >
          {'← '}
          {t('Back to connectors')}
        </button>
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          <ConnectorInvoker connectorId={activeConnectorId} />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      {connectors.map((c) => (
        <button
          key={c.id}
          onClick={() => setActiveConnectorId(c.id)}
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--color-surface)',
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{c.name}</span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-subtle)' }}>
            {c.operations.length} {c.operations.length === 1 ? t('operation') : t('operations')}
          </span>
        </button>
      ))}
    </div>
  );
}

function ToggleSwitch({ checked, disabled, label }: ToggleSwitchProps) {
  return (
    <span
      className={[
        styles.toggle,
        checked ? styles.toggleOn : '',
        disabled ? styles.toggleDisabled : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      aria-label={label}
    >
      <span className={styles.toggleThumb} aria-hidden="true" />
    </span>
  );
}
